import { ref, nextTick } from 'vue'
import { showToast } from 'vant'
import { useI18n } from 'vue-i18n'
import { apiGetEnabledAITextToVideoCategories, apiGetRecommendCreations } from '@/apis/theater'
import type { IResCreationCategory } from '@/types/apis/theater'
import type { ICreationFeedItem } from '@/components/feed-player/types'
import { formatCreationFeedItem } from '@/components/feed-player/data-adapter'

const MAX_CACHED_ITEMS = 1000
const LOAD_MORE_THRESHOLD = 3
const ALL_CREATION_CATEGORY_ID = 0

export function useCreationFeed() {
  const { t, locale } = useI18n()

  const categories = ref<IResCreationCategory[]>([createAllCategory()])
  const activeCategoryId = ref(ALL_CREATION_CATEGORY_ID)
  const categoriesLoading = ref(false)
  const categoriesLoaded = ref(false)
  const items = ref<ICreationFeedItem[]>([])
  const loading = ref(false)
  const hasMore = ref(true)
  const loaded = ref(false)
  const currentIndex = ref(0)
  const cursorId = ref<number | null>(null)

  let requestVersion = 0

  function createAllCategory(): IResCreationCategory {
    return {
      Id: ALL_CREATION_CATEGORY_ID,
      Name: t('component.theater.allCategories'),
      NameEn: 'All',
      Icon: null,
      SortOrder: -1,
      Status: 1,
      CreateTime: '',
      UpdateTime: null,
    }
  }

  const sortCategories = (items: IResCreationCategory[]) => {
    return [...items].sort((a, b) => {
      const sortByOrder = (a.SortOrder ?? 0) - (b.SortOrder ?? 0)
      if (sortByOrder !== 0) return sortByOrder
      return a.Id - b.Id
    })
  }

  const getCategoryTitle = (item: IResCreationCategory) => {
    if (item.Id === ALL_CREATION_CATEGORY_ID) return t('component.theater.allCategories')
    if (locale.value.startsWith('en')) return item.NameEn || item.Name
    return item.Name || item.NameEn
  }

  const reset = () => {
    requestVersion += 1
    items.value = []
    loading.value = false
    hasMore.value = true
    loaded.value = false
    currentIndex.value = 0
    cursorId.value = null
  }

  const loadCategories = async () => {
    if (categoriesLoading.value) return

    try {
      categoriesLoading.value = true
      const response = await apiGetEnabledAITextToVideoCategories()

      if (response.IsSuccess && response.Body) {
        categories.value = [createAllCategory(), ...sortCategories(response.Body)]
        categoriesLoaded.value = true
        return
      }

      showToast(t('home.errors.loadFailed'))
    } catch (error) {
      console.error('加载创作分类失败:', error)
      showToast(t('common.errors.networkError'))
    } finally {
      categoriesLoading.value = false
    }
  }

  const changeCategory = (categoryId: number, scrollToCategory?: (id: number) => void) => {
    if (activeCategoryId.value === categoryId) return
    activeCategoryId.value = categoryId
    if (scrollToCategory) {
      void nextTick(() => scrollToCategory(categoryId))
    }
    reset()
  }

  const loadMore = async (playerRef?: { jumpToIndex: (index: number) => void }) => {
    if (loading.value || !hasMore.value) return
    const currentRequestVersion = ++requestVersion

    try {
      loading.value = true
      const selectedCategoryId = activeCategoryId.value

      const response = await apiGetRecommendCreations({
        PageSize: 10,
        LastEpisodeId: cursorId.value,
        VideoCategoryId: selectedCategoryId === ALL_CREATION_CATEGORY_ID ? null : selectedCategoryId,
      })

      if (currentRequestVersion !== requestVersion) return

      if (response.IsSuccess && response.Body) {
        const rawItems = response.Body
        if (rawItems.length === 0) {
          hasMore.value = false
          loaded.value = true
          return
        }

        cursorId.value = rawItems[rawItems.length - 1]?.Id || cursorId.value

        const nextItems = rawItems.map(item => formatCreationFeedItem(item))

        const currentIdx = Number(currentIndex.value ?? 0)

        items.value.push(...nextItems)
        loaded.value = true

        if (items.value.length > MAX_CACHED_ITEMS) {
          const keepFromIndex = Math.max(0, currentIdx - 10)
          items.value = items.value.slice(keepFromIndex)

          const newIndex = Math.max(0, currentIdx - keepFromIndex)
          currentIndex.value = newIndex
          playerRef?.jumpToIndex(newIndex)
        }
      } else {
        showToast(t('home.errors.loadFailed'))
      }
    } catch (error) {
      console.error('加载创作视频失败:', error)
      showToast(t('common.errors.networkError'))
    } finally {
      if (currentRequestVersion === requestVersion) {
        loading.value = false
      }
    }
  }

  const handleIndexChange = (index: number, playerRef?: { jumpToIndex: (index: number) => void }) => {
    currentIndex.value = index
    const remaining = items.value.length - index
    if (remaining <= LOAD_MORE_THRESHOLD && !loading.value && hasMore.value) {
      void loadMore(playerRef)
    }
  }

  return {
    categories,
    activeCategoryId,
    categoriesLoading,
    categoriesLoaded,
    items,
    loading,
    hasMore,
    loaded,
    currentIndex,
    getCategoryTitle,
    loadCategories,
    changeCategory,
    loadMore,
    reset,
    handleIndexChange,
  }
}
